import { get } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import type { AccessInventorySummary } from '@uncain/shared';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

const REFERENCE_TYPE_LABELS: Record<'secret' | 'token', string> = {
	secret: 'Secret',
	token: 'Token',
};

const SCOPE_LABELS: Record<string, string> = {
	workflow_env: 'Workflow env',
	job_env: 'Job env',
	job_with: 'Job input',
	job_secrets: 'Job secrets',
	job_if: 'Job condition',
	step_env: 'Step env',
	step_with: 'Step input',
	step_run: 'Run command',
	step_if: 'Step condition',
};

export function InventoryAccessPage() {
	const [search, setSearch] = useState('');
	const { data, isLoading, error } = useQuery<{ access: AccessInventorySummary[] }>({
		queryKey: queryKeys.inventory.access(),
		queryFn: () => get<{ access: AccessInventorySummary[] }>('/inventory/access'),
	});

	const accessItems = data?.access ?? [];
	const filtered = useMemo(() => {
		const term = search.trim().toLowerCase();
		if (term.length === 0) {
			return accessItems;
		}
		return accessItems.filter((item) =>
			[
				item.displayName,
				item.referenceType,
				...item.repositories,
				...item.workflows,
				...item.scopes,
			].some((value) => value.toLowerCase().includes(term)),
		);
	}, [accessItems, search]);

	if (isLoading) {
		return <div className="text-muted-foreground">Loading access inventory...</div>;
	}

	if (error) {
		return <div className="text-destructive">Failed to load access inventory.</div>;
	}

	return (
		<div className="space-y-6">
			<div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
				<div>
					<p className="text-sm uppercase tracking-[0.18em] text-muted-foreground">Inventory</p>
					<h1 className="text-2xl font-bold text-foreground">Access</h1>
					<p className="text-sm text-muted-foreground">
						Named secret and token references used across workflows in the active organization.
					</p>
				</div>
				<div className="w-full md:w-80">
					<label className="sr-only" htmlFor="inventory-access-search">
						Search access references
					</label>
					<input
						id="inventory-access-search"
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						placeholder="Search references, repos, workflows..."
						className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary/40"
					/>
				</div>
			</div>

			{filtered.length === 0 ? (
				<div className="rounded-lg border border-border bg-card p-8 text-center text-muted-foreground">
					{accessItems.length === 0
						? 'No named access references are available in this org inventory yet.'
						: 'No access references match the current search.'}
				</div>
			) : (
				<div className="space-y-3">
					{filtered.map((item) => (
						<AccessCard key={item.id} access={item} />
					))}
				</div>
			)}
		</div>
	);
}

function AccessCard({ access }: { access: AccessInventorySummary }) {
	return (
		<Link
			to={`/inventory/access/detail/${encodeURIComponent(access.id)}`}
			className="block rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/30"
		>
			<div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div className="min-w-0 space-y-2">
					<div className="flex flex-wrap items-center gap-2">
						<h2 className="font-semibold text-foreground">{access.displayName}</h2>
						<Badge>{REFERENCE_TYPE_LABELS[access.referenceType]}</Badge>
					</div>

					<p className="text-sm text-muted-foreground">
						{access.repositoryCount} repos · {access.workflowCount} workflows · {access.jobCount}{' '}
						jobs · {access.usageCount} usages
					</p>

					<div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
						{access.scopes.map((scope) => (
							<span key={scope} className="rounded bg-muted px-2 py-1">
								{SCOPE_LABELS[scope] ?? scope}
							</span>
						))}
					</div>
				</div>

				<div className="grid grid-cols-2 gap-3 text-sm lg:min-w-72">
					<Stat label="Reference" value={access.referenceName} />
					<Stat label="Type" value={REFERENCE_TYPE_LABELS[access.referenceType]} />
					<Stat
						label="Findings"
						value={String(access.findingCount)}
						tone={access.findingCount > 0 ? 'danger' : 'neutral'}
					/>
					<Stat label="Detail" value="Open inventory view" tone="info" />
				</div>
			</div>
		</Link>
	);
}

function Stat({
	label,
	value,
	tone = 'neutral',
}: {
	label: string;
	value: string;
	tone?: 'neutral' | 'info' | 'danger';
}) {
	return (
		<div className="rounded-lg border border-border/70 bg-background/60 p-3">
			<p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
			<p
				className={cn(
					'mt-1 text-sm font-medium',
					tone === 'info' && 'text-primary',
					tone === 'danger' && 'text-severity-critical',
					tone === 'neutral' && 'text-foreground',
				)}
			>
				{value}
			</p>
		</div>
	);
}

function Badge({ children }: { children: React.ReactNode }) {
	return (
		<span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
			{children}
		</span>
	);
}
